// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { PaymentDTO } from "../../../../../../../io/hyperify/core/store/types/payment/PaymentDTO";
import {
    MY_FUTURE_PAYMENT_LIST_CLASS_NAME,
} from "../../../../../core/constants/className";
import {
    T_COMMON_OPEN,
    T_DATE_FORMAT,
    T_MY_PAYMENT_LIST_VIEW_NO_CLIENT,
    T_MY_PAYMENT_LIST_VIEW_TITLE,
    T_MY_PAYMENT_LIST_VIEW_NO_RESULTS_FOUND,
    T_MY_PAYMENT_LIST_VIEW_TABLE_PAYMENT_ID_LABEL,
    T_MY_PAYMENT_LIST_VIEW_TABLE_DESCRIPTION_LABEL,
    T_MY_PAYMENT_LIST_VIEW_TABLE_START_DATE_LABEL,
    T_MY_PAYMENT_LIST_VIEW_TABLE_END_DATE_LABEL,
} from "../../../../../core/constants/translation";
import { useParams } from "react-router-dom";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { useCallback } from "react";
import { GET_MY_CLIENT_PAYMENT_ITEM_ROUTE } from "../../../../../core/constants/route";
import { RouteService } from "../../../../../../../io/hyperify/frontend/services/RouteService";
import { LogService } from "../../../../../../../io/hyperify/core/LogService";
import { Card } from "../../../../../../../io/hyperify/frontend/components/card/Card";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import { Table } from "../../../../../../../io/hyperify/frontend/components/table/Table";
import { TableHeader } from "../../../../../../../io/hyperify/frontend/components/table/TableHeader";
import { TableHeaderColumn } from "../../../../../../../io/hyperify/frontend/components/table/TableHeaderColumn";
import { TableBody } from "../../../../../../../io/hyperify/frontend/components/table/TableBody";
import { map } from "../../../../../../../io/hyperify/core/functions/map";
import { TableRow } from "../../../../../../../io/hyperify/frontend/components/table/TableRow";
import { TextColumn } from "../../../../../../../io/hyperify/frontend/components/table/columns/text/TextColumn";
import { TimeColumn } from "../../../../../../../io/hyperify/frontend/components/table/columns/time/TimeColumn";
import { TableColumn } from "../../../../../../../io/hyperify/frontend/components/table/TableColumn";
import { Button } from "../../../../../../../io/hyperify/frontend/components/button/Button";
import { useMyPaymentList } from "../../../../hooks/useMyPaymentList";
import { ButtonStyle } from "../../../../../../../io/hyperify/core/frontend/button/ButtonStyle";
import "./MyFuturePaymentListView.scss";

const LOG = LogService.createLogger( 'MyFuturePaymentListView' );

export interface MyFuturePaymentListProps {
    readonly className? : string;
    readonly t? : TranslationFunction;
}

export function MyFuturePaymentListView ( props : MyFuturePaymentListProps ) {
    const t : TranslationFunction = props?.t ?? (( key : string ) : string => key);
    const className = props?.className;
    const params = useParams();
    const clientId = params?.clientId;
    const [ list/*, refreshList*/ ] = useMyPaymentList( clientId );
    const isLoading = list?.length === undefined;
    const listCount = list?.length ?? 0;
    const onItemClick = useCallback(
        ( itemId : string ) => {
            const route = GET_MY_CLIENT_PAYMENT_ITEM_ROUTE( clientId ?? '', itemId );
            LOG.debug( `Going to inventory route: `, route );
            RouteService.setRoute( route );
        },
        [
            clientId,
        ],
    );
    return (
        <div
            className={
                MY_FUTURE_PAYMENT_LIST_CLASS_NAME
                + (className ? ` ${ className }` : '')
            }
        >{ !clientId ? (
            <p>{ t( T_MY_PAYMENT_LIST_VIEW_NO_CLIENT ) }</p>
        ) : (
            <>
                <h3 className={ `${ MY_FUTURE_PAYMENT_LIST_CLASS_NAME }-title` }>{ t( T_MY_PAYMENT_LIST_VIEW_TITLE ) }</h3>
                <div className={ `${ MY_FUTURE_PAYMENT_LIST_CLASS_NAME }-details` }>
                    <Card>
                        { isLoading ? <Loader /> : (
                            <>
                                { listCount === 0 ? (
                                    <p>{ t( T_MY_PAYMENT_LIST_VIEW_NO_RESULTS_FOUND ) }</p>
                                ) : (
                                    <Table>
                                        <TableHeader>
                                            <TableHeaderColumn>{ t( T_MY_PAYMENT_LIST_VIEW_TABLE_PAYMENT_ID_LABEL ) }</TableHeaderColumn>
                                            <TableHeaderColumn>{ t( T_MY_PAYMENT_LIST_VIEW_TABLE_START_DATE_LABEL ) }</TableHeaderColumn>
                                            <TableHeaderColumn>{ t( T_MY_PAYMENT_LIST_VIEW_TABLE_END_DATE_LABEL ) }</TableHeaderColumn>
                                            <TableHeaderColumn>{ t( T_MY_PAYMENT_LIST_VIEW_TABLE_DESCRIPTION_LABEL ) }</TableHeaderColumn>
                                            <TableHeaderColumn>
                                            </TableHeaderColumn>
                                        </TableHeader>
                                        <TableBody>{ map(
                                            list,
                                            ( item : PaymentDTO ) => {
                                                const paymentId = item?.paymentId;
                                                const itemClick = () => onItemClick( paymentId );
                                                return (
                                                    <TableRow
                                                        key={ `inventory-${ paymentId }` }
                                                        click={ itemClick }
                                                    >
                                                        <TextColumn value={ paymentId } />
                                                        <TimeColumn
                                                            value={ item?.startDate }
                                                            format={ t( T_DATE_FORMAT ) }
                                                        />
                                                        <TimeColumn
                                                            value={ item?.endDate }
                                                            format={ t( T_DATE_FORMAT ) }
                                                        />
                                                        <TextColumn value={ item?.description } />
                                                        <TableColumn>
                                                            <Button
                                                                click={ itemClick }
                                                                style={ ButtonStyle.SECONDARY }
                                                            >{ t( T_COMMON_OPEN ) }</Button>
                                                        </TableColumn>
                                                    </TableRow>
                                                );
                                            },
                                        ) }
                                        </TableBody>
                                    </Table>
                                ) }
                            </>
                        ) }</Card>
                </div>
            </>
        ) }</div>
    );
}
